import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from "jspdf";
import './RentalAgreementView.css';
import { baseURL, appName, tenantId } from '../../../config/api';
import { getReservationSummaryByPaymentRequestUniqeid } from '../../../services/paymentRequest';
import usePleaseWait from '../../../hooks/usePleaseWait';

const RentalAgreementView = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search)
  const uniqeid = queryParams?.get('id') ?? 0;

  const navigate = useNavigate();
  const { setPleasWaitPopup }: any = usePleaseWait();

  const [reservationId, setReservationId] = useState<number>(0);
  const [rentalAgreementHtmlCode, setRentalAgreementHtmlCode] = useState<string>("");
  const [initialsURL, setInitialsURL] = useState<any | null>();

  const [isLoadingTheRentalAgrement, setIsLoadingTheRentalAgrement] = useState<boolean>(false);
  const htmlRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (uniqeid) {
      setPleasWaitPopup(true);
      getReservationSummaryByPaymentRequestUniqeid(uniqeid, tenantId)
        .then(res => {
          if (res?.paidAt === null || res?.paidAt === "") {
            navigate(`/public/booking?id=${uniqeid}`, { replace: true });
          }
          setReservationId(res?.reservationId);
          setInitialsURL(res?.initials);

        })
        .catch((error) => {
          setIsLoadingTheRentalAgrement(true);
          setPleasWaitPopup(false);
          if (error?.response?.status === 400) {
            if (error?.response?.data === "Link is expired or invalid.") {
              navigate(`/public/booking/not-available?id=${uniqeid}`, { replace: true });
            } else {
              navigate(`not-found`, { replace: true });
            }
          }
          console.log(error);
        });
    } else {
      navigate(`not-found`, { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqeid])

  useEffect(() => {
    if (reservationId > 0) {
      const fetchPdf = async () => {
        try {
          const response = await axios.get(
            `${baseURL}rentalAgreementPdf?reservationId=${reservationId}`,
            {
              responseType: 'text', // Fetching HTML content (not binary data)
              headers: {
                'X-Tenant-ID': tenantId,
              }
            }
          );

          setRentalAgreementHtmlCode(response?.data || "");
        } catch (error) {
          setIsLoadingTheRentalAgrement(true);
          setPleasWaitPopup(false);
          setRentalAgreementHtmlCode("")
          console.log(error);
        }
      }

      fetchPdf();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId])

  useEffect(() => {
    if (rentalAgreementHtmlCode) {
      const convertPdf = async () => {
        try {
          const getImageBase64 = async (imgURL: string) => {
            const response = await fetch(imgURL, {
              headers: {
                'X-Tenant-ID': tenantId,
              }
            });
            const blob = await response.blob();
            const reader = new FileReader();

            return new Promise((resolve, reject) => {
              reader.onloadend = () => {
                resolve(reader.result);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          };

          const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
            compress: true
          });

          // Set PDF properties
          doc.setProperties({
            title: `RentalAgreement${reservationId}.pdf`,
            author: "Website",
            subject: "Rental Agreement",
            keywords: "rental, agreement",
            creator: appName
          });

          let base64Image: string | null = null;
          if (initialsURL?.fileName) {
            const imgURL = `${initialsURL?.fileName}`;
            base64Image = await getImageBase64(imgURL) as string;
          }

          const addFooterImage = (doc: jsPDF, base64Image: string | null) => {
            if (base64Image) {
              const pageHeight = doc.internal.pageSize.height;
              const pageWidth = doc.internal.pageSize.width

              const reducedWidth = 8;
              const reducedHeight = 4;

              const xPosition = pageWidth - 24;
              const yPosition = pageHeight - reducedHeight - 2;

              doc.addImage(base64Image, 'JPEG', xPosition, yPosition, reducedWidth, reducedHeight);
            }
          }

          const addPageNumber = (doc: jsPDF, pageIndex: number) => {
            const pageHeight = doc.internal.pageSize.height;
            const pageWidth = doc.internal.pageSize.width;

            const totalPages = doc.internal.pages.length - 1;

            const pageNumberText = `Page ${pageIndex} of ${totalPages}`;

            const xPosition = pageWidth / 2 - doc.getTextWidth(pageNumberText) / 2;
            const yPosition = pageHeight - 2;

            doc.setFontSize(8);
            doc.text(pageNumberText, xPosition, yPosition);
          }

          doc.html(`<div class="js-pdf-wrapper">${rentalAgreementHtmlCode}</div>`, {
            callback: async (doc: jsPDF) => {
              const totalPages = doc.internal.pages.length;

              for (let i = 1; i < totalPages; i++) {
                addFooterImage(doc, base64Image);
                doc.setPage(i);
                addPageNumber(doc, i);
              }

              const iframe = document.createElement('iframe');
              iframe.src = doc.output('bloburi')?.toString();
              iframe.setAttribute('style', 'width:100%;height:100vh');

              if (htmlRef.current) {
                htmlRef.current.innerHTML = '';
                htmlRef.current.appendChild(iframe);
                setIsLoadingTheRentalAgrement(true);
                setPleasWaitPopup(false);
              }
            },
            margin: [6, 5, 7, 5],
            x: 0,
            y: 0,
            width: 200,
            windowWidth: 1024,
            filename: `RentalAgreement${reservationId}.pdf`,
            autoPaging: "text"
          });
        } catch (error) {
          setIsLoadingTheRentalAgrement(true);
          setPleasWaitPopup(false);
          console.log("Error fetching or converting PDF:", error);
        }
      }
      convertPdf();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalAgreementHtmlCode])


  return (
    <div className="w-full min-h-screen overflow-hidden">
      {
        !isLoadingTheRentalAgrement && (
          <div className="w-full min-h-screen flex items-center justify-center mt-2">
            <p className="text-sm text-gray-400">Loading...</p>
          </div>
        )
      }

      <div className="relative">
        <div className="w-full min-h-screen" ref={htmlRef}></div>
      </div>
    </div>
  )
}

export default RentalAgreementView